import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useState } from 'react'
import { saveBlobAsFile } from '../../../../api/apiUtils.js'
import {
  createSectionKeyword,
  deleteSectionKeyword,
  downloadSectionKeywordsExcel,
  getSectionKeywordsList,
  updateSectionKeyword,
} from '../../../../api/SectionKeywordApi.js'
import DownloadIcon from '../../../../assets/icons/DownloadIcon'
import SearchIconBtn from '../../../../assets/icons/SearchIconBtn.jsx'
import {
  DEFAULT_PAGE,
  getValidCriteria,
  sanitizeForLog,
} from '../../../../constants'
import { GLOBAL_CONSTANTS } from '../../../../constants/globalConstants.js'
import { useSnackbar } from '../../../../contexts/SnackbarProvider'
import CommonModal from '../../../common/CommonModal'
import CommonTable from '../../../common/CommonTable'
import ConfirmationDialog from '../../../common/ConfirmationDialog.jsx'
import Search from '../../../common/Search'
import KeyFilter from '../common/KeyFilter'
import { columns, searchFields } from '../configs/sectionKeywordTableConfig'
import AddSectionKeyword from './AddSectionKeyword'

const SectionKeyword = ({ roleConfig }) => {
  // Sample data - replace with actual data from your API/context
  const [showTable, setShowTable] = useState(false)
  const [tableData, setTableData] = useState([])
  const [showAddKeywordDialog, setshowAddKeywordDialog] = useState(false)
  const [startDownloading, setStartDownloading] = useState(false)
  const [loading, setLoading] = useState(true)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)
  const [totalItems, setTotalItems] = useState(100)

  // Search state
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchCriteria, setSearchCriteria] = useState([
    {
      id: 1,
      logicalOperator: 'Where',
      field: searchFields[0]?.id || '',
      value: '',
    },
  ])

  const [keyFilterData, setKeyFilterData] = useState([])
  const { showSnackbar } = useSnackbar()
  const [keywordToDelete, setKeywordDeleted] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Form - Initialize as object, not array
  const [keywordsForm, setKeywordsForm] = useState({
    id: null,
    sectionCode: '',
    broadHead: '',
    keywords: '',
  })
  const [formErrors, setFormErrors] = useState({})
  const [formLoading, setFormLoading] = useState(false)
  const [tanMasterId, setTanMasterId] = useState(null)

  const loadSectionKeywordsData = async (
    page = 1,
    pageSize = itemsPerPage,
    searchList = [],
    tandID = tanMasterId
  ) => {
    setLoading(true)
    setShowTable(true)

    try {
      const payload = {
        pageFilter: {
          currentPage: page,
          includeCount: true,
          pageSize: pageSize,
          sortField: 'sectionKeyword',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: false,
        searchCOList: searchList,
      }
      let resp = await getSectionKeywordsList(payload, tandID)

      setTableData(resp.data.content)
      setTotalItems(resp.data.totalElements)
    } catch (error) {
      console.error('Failed to load section keywords:')
    } finally {
      setLoading(false)
    }
  }

  const handleViewClick = data => {
    setTanMasterId(data.tanMasterUuid)
    loadSectionKeywordsData(1, itemsPerPage, [], data.tanMasterUuid)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const handleCloseAddKeywordDialog = () => {
    setshowAddKeywordDialog(false)
    setKeywordsForm({
      id: null,
      sectionCode: '',
      broadHead: '',
      keywords: '',
    })
    setFormErrors({})
  }

  const handleEdit = rowData => {
    setKeywordsForm({
      id: rowData.id,
      sectionCode: rowData.sectionKeyword || '',
      broadHead: rowData.broadHead || '',
      keywords: rowData.keyword || '',
    })
    setshowAddKeywordDialog(true)
  }

  const handleSaveKeyword = async data => {
    setFormLoading(true)

    try {
      if (data.id) {
        // Edit mode
        const payload = {
          keyword: data?.keywords,
          sectionKeyword: data?.sectionCode,
          tanMasterId: tanMasterId,
          broadHead: data?.broadHead,
        }
        const response = await updateSectionKeyword(payload, data.id)
        if (response.success) {
          loadSectionKeywordsData(1, itemsPerPage)
          setKeywordsForm({
            id: null,
            sectionCode: '',
            broadHead: '',
            keywords: '',
          })
          showSnackbar(response.data.data, 'success')
          setshowAddKeywordDialog(false)
        } else {
          showSnackbar(response?.error || 'Update failed', 'error')
        }
      } else {
        // Add mode
        const payload = {
          keyword: data?.keywords,
          sectionKeyword: data?.sectionCode,
          tanMasterId: tanMasterId,
          broadHead: data?.broadHead,
        }
        const response = await createSectionKeyword(payload)
        if (response.success) {
          // Reload the table data after successful creation
          loadSectionKeywordsData(1, itemsPerPage)

          // Reset form and close dialog
          setKeywordsForm({
            id: null,
            sectionCode: '',
            broadHead: '',
            keywords: '',
          })
          showSnackbar(response.data.data, 'success')
          setshowAddKeywordDialog(false)
        } else {
          showSnackbar(response?.error || 'Create failed', 'error')
        }
      }
    } catch (error) {
      setFormErrors({ general: error.message })
      console.error('Error saving keyword:')
    } finally {
      setFormLoading(false)
    }
  }

  const handleChangeKeywordForm = updatedData => {
    setKeywordsForm(prev => ({
      ...prev,
      ...updatedData,
    }))
    // Clear errors for the field that was just updated
    if (formErrors) {
      const newErrors = { ...formErrors }
      Object.keys(updatedData).forEach(key => {
        if (newErrors[key]) {
          delete newErrors[key]
        }
      })
      setFormErrors(newErrors)
    }
  }

  const handlePageChange = page => {
    setCurrentPage(page)
    loadSectionKeywordsData(
      page,
      itemsPerPage,
      getValidCriteria(searchCriteria)
    )
  }

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
    loadSectionKeywordsData(
      1,
      newItemsPerPage,
      getValidCriteria(searchCriteria)
    )
  }

  const handleCloseSearch = () => {
    setSearchOpen(false)
    setAnchorEl(null)
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
  }

  const handleDownload = async () => {
    if (totalItems === 0) {
      showSnackbar('No Data Found', 'info')
      return
    }

    setStartDownloading(true)
    try {
      const payload = {
        pageFilter: {
          currentPage: 1,
          includeCount: true,
          pageSize: DEFAULT_PAGE,
          sortField: 'sectionKeyword',
          sortOrder: 'ASC',
        },
        requestFilter: [],
        isReportDownload: true,
        searchCOList: getValidCriteria(searchCriteria),
        tanMasterId,
      }

      const blob = await downloadSectionKeywordsExcel(payload)
      saveBlobAsFile(blob, 'section_keywords.xlsx') // ✅ centralized safe helper
    } catch (error) {
      console.error(
        'Failed to download Section Keywords Excel:',
        sanitizeForLog(error)
      )
    } finally {
      setStartDownloading(false)
    }
  }

  const handleAddKeyword = () => {
    setKeywordsForm({
      id: null,
      sectionCode: '',
      broadHead: '',
      keywords: '',
    })
    setFormErrors({})
    setshowAddKeywordDialog(true)
  }

  const handleSearch = event => {
    setSearchOpen(!searchOpen)
    setAnchorEl(event.currentTarget)
  }

  const handleApplySearch = async searchList => {
    // Map fieldName to field for UI
    const mappedCriteria = searchList.map((item, idx) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mappedCriteria)
    setCurrentPage(1)
    await loadSectionKeywordsData(1, itemsPerPage, searchList)
    setSearchOpen(false)
  }

  const handleResetSearch = async () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: searchFields[0]?.id || '',
        value: '',
      },
    ])
    setCurrentPage(1)
    await loadSectionKeywordsData(1, itemsPerPage, [])
    setSearchOpen(false)
  }

  const handleDelete = rule => {
    setKeywordDeleted(rule)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (keywordToDelete) {
      try {
        // api call
        const resp = await deleteSectionKeyword(
          tanMasterId,
          keywordToDelete?.id
        )
        if (resp.success) {
          showSnackbar(resp?.data?.data, 'success')
          setDeleteDialogOpen(false)
          setKeywordDeleted(null)
          loadSectionKeywordsData(1, itemsPerPage, [])
        } else {
          showSnackbar(resp?.error, 'error')
        }
      } catch (error) {
        showSnackbar(error.message || 'Failed to delete keyword', 'error')
      }
    }
  }

  return (
    <Box>
      {/* Filter Section */}
      <KeyFilter
        handleViewClick={handleViewClick}
        setKeyFilterData={setKeyFilterData}
      />

      {/* Table Section with Spacing */}
      {showTable && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: '32px',
              py: '12px',
            }}
          >
            {/* Left side - Title */}
            <Typography
              variant="h6"
              sx={{
                fontSize: '20px',
                color: 'var(--forbrand-secondary)',
                fontWeight: 500,
              }}
            >
              Section Keywords
            </Typography>

            {/* Right side - Action buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={GLOBAL_CONSTANTS.DOWNLOAD_EXCEL}>
                <span className="disabled:cursor-not-allowed  rounded-full">
                  <IconButton
                    onClick={handleDownload}
                    disabled={loading || startDownloading}
                  >
                    <DownloadIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={GLOBAL_CONSTANTS.SEARCH}>
                <IconButton onClick={handleSearch}>
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>

              {roleConfig.canCreate && (
                <>
                  <Typography
                    sx={{
                      height: '16px',
                      borderLeft: '1px solid var(--forbrand-color)',
                      marginRight: '6px',
                    }}
                  ></Typography>

                  <Button
                    variant="contained"
                    onClick={handleAddKeyword} // ✅ Use the new handler
                    sx={{
                      backgroundColor: 'var(--forbrand-bg-variant)',
                      color: 'var(--forbrand-brand-varient)',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '16px',
                    }}
                  >
                    Add
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box>
            <CommonTable
              columns={columns}
              data={tableData}
              loading={loading}
              onEdit={roleConfig.canCreate && handleEdit}
              onDelete={handleDelete}
              emptyMessage="No data available"
              showFooter={true}
              totalItems={totalItems}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
              showPageNumbers={true}
            />
          </Box>
        </>
      )}

      {/* Add Section Dialog */}
      <CommonModal
        open={showAddKeywordDialog}
        onClose={handleCloseAddKeywordDialog}
        title={'Add Keyword'}
        showCloseButton={true}
        maxWidth={'sm'}
        sx={{
          '& .MuiDialogContent-root': {
            padding: 0,
          },
          '& .MuiDialogTitle-root': {
            display: 'none',
          },
        }}
      >
        <AddSectionKeyword
          formData={keywordsForm}
          formErrors={formErrors}
          formLoading={formLoading}
          onSubmit={handleSaveKeyword}
          onClose={handleCloseAddKeywordDialog}
          onChange={handleChangeKeywordForm}
          tanMasterId={tanMasterId}
          roleConfig={roleConfig}
        />
      </CommonModal>

      <Search
        open={searchOpen}
        onClose={handleCloseSearch}
        onApply={handleApplySearch}
        onReset={handleResetSearch}
        anchorEl={anchorEl}
        fields={searchFields}
        placement="bottom-start"
        setSearchCriteria={setSearchCriteria}
        searchCriteria={searchCriteria}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this section keyword?"
        message={`This action cannot be undone. Do you want to continue?`}
        confirmText="Proceed"
        itemType="rule"
        type="delete"
        showCancelButton={true}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  )
}

export default SectionKeyword
